// WebSocket client + event dispatcher. Cada mensagem que chega vira
// um `WSMessage` no store `lastMessage` — outras stores (lobby, match)
// subscrevem e reagem aos eventos relevantes.
//
// Padrao escolhido (vs EventEmitter classico): aproveita a reatividade
// do Svelte. Uma store recebe o envelope, deriva seu state, e o
// componente que usa `$lobby` se atualiza sozinho. Sem listeners
// manuais pra remover no unmount.

import { writable, get } from 'svelte/store';
import { token } from './auth';
import { api } from '../lib/api';
import { firebaseAuth } from '../lib/firebase';
import { DEFAULT_API_BASE } from '../lib/constants';

export type WSStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export type WSMessage = {
  event: string;
  data: Record<string, unknown>;
};

export const wsStatus = writable<WSStatus>('idle');
// Recebe o ULTIMO envelope WS. Stores subscritas comparam previous
// vs new pra detectar transicoes (ex: lobby store ve queue:enrolled
// pra abrir modal de searching).
export const lastMessage = writable<WSMessage | null>(null);

let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let reconnectAttempt = 0;
const RECONNECT_MAX_MS = 30000;

// ── Watchdog de conexao meio-morta ─────────────────────────────────
// Wifi caindo/trocando SEM FIN deixa o socket "open" sem trafegar nada
// — o cliente nao percebe (os ping/pong de PROTOCOLO do servidor sao
// respondidos pelo browser automaticamente, invisiveis pro JS) e a mesa
// congela ("oponente jogou e nao apareceu" com aba VISIVEL, Leonardo
// 2026-06-11). Solucao app-level: manda {event:"ping"} a cada 25s; o
// backend responde "pong". Se ficar 40s+ sem RECEBER nada, forca
// close() — a maquina de reconnect existente religa e o resync via
// wsStatus recompoe a mesa.
const PING_EVERY_MS = 25_000;
const SILENCE_LIMIT_MS = 40_000;
let lastMessageAt = 0;
let watchdogTimer: number | null = null;

function startWatchdog() {
  stopWatchdog();
  lastMessageAt = Date.now();
  watchdogTimer = window.setInterval(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const silence = Date.now() - lastMessageAt;
    if (silence > SILENCE_LIMIT_MS) {
      console.warn(`ws: ${Math.round(silence / 1000)}s sem mensagens — conexao meio-morta, forcando reconnect`);
      try { socket.close(); } catch { /* noop */ }
      return;
    }
    send('ping');
  }, PING_EVERY_MS);
}

function stopWatchdog() {
  if (watchdogTimer !== null) {
    clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
}

async function buildWsUrl(): Promise<string> {
  const base = DEFAULT_API_BASE;
  // Forca refresh do ID token do Firebase antes de conectar. Sem isso,
  // reconexoes apos 1h carregam token expirado e o backend rejeita 401
  // no handshake — usuario cai em loop ate o sessionExpired disparar.
  // currentUser.getIdToken() retorna cached se valido, refresh
  // automatico se expirando.
  let tk = get(token);
  if (firebaseAuth.currentUser) {
    try {
      tk = await firebaseAuth.currentUser.getIdToken();
    } catch (err) {
      console.warn('ws: getIdToken falhou, usando token cached —', err);
    }
  }
  const u = new URL(base);
  const scheme = u.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${u.host}/ws?token=${encodeURIComponent(tk)}`;
}

export async function connect() {
  if (!get(token)) {
    console.warn('ws.connect: sem token, abortando');
    return;
  }
  disconnect();
  wsStatus.set('connecting');
  const url = await buildWsUrl();
  socket = new WebSocket(url);

  socket.addEventListener('open', () => {
    reconnectAttempt = 0;
    wsStatus.set('open');
    startWatchdog();
  });

  socket.addEventListener('message', (e) => {
    lastMessageAt = Date.now(); // qualquer trafego = conexao viva
    try {
      const env = JSON.parse(e.data);
      if (env && typeof env.event === 'string') {
        if (env.event === 'pong') return; // liveness — nao interessa as stores
        lastMessage.set({ event: env.event, data: env.data ?? {} });
      }
    } catch (err) {
      console.warn('ws: msg nao-JSON', e.data, err);
    }
  });

  socket.addEventListener('close', () => {
    stopWatchdog();
    wsStatus.set('closed');
    socket = null;
    if (!get(token)) return;
    // Antes de reconectar, ping HTTP pra distinguir queda de rede /
    // redeploy (token valido) de token expirado (Cloud Run cold start
    // zerou as sessions in-memory). Se 401, api() ja dispara
    // sessionExpired e App.svelte volta pra tela de login — nao adianta
    // reconectar em loop pegando 401 toda vez.
    (async () => {
      try {
        await api.get('/users/me');
        // Token valido: backend de redeploy, reconnect normal.
        if (!get(token)) return; // sessionExpired pode ter rodado entre await e check
        const delay = Math.min(RECONNECT_MAX_MS, 1000 * Math.pow(2, reconnectAttempt));
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      } catch (_err) {
        // 401 ja disparou sessionExpired via api(); outros erros (rede,
        // DNS, 5xx) caem aqui — ainda tentamos reconectar pra nao prender
        // usuario offline.
        if (!get(token)) return; // sessionExpired limpou — para aqui
        const delay = Math.min(RECONNECT_MAX_MS, 1000 * Math.pow(2, reconnectAttempt));
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      }
    })();
  });

  socket.addEventListener('error', () => {
    wsStatus.set('error');
  });
}

export function disconnect() {
  stopWatchdog();
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    try { socket.close(); } catch { /* noop */ }
    socket = null;
  }
}

export function send(event: string, data: Record<string, unknown> = {}): boolean {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    console.warn('ws.send: socket nao aberto', event);
    return false;
  }
  socket.send(JSON.stringify({ event, data }));
  return true;
}
